<template>
  <main id="main" class="main">
    <div class="pagetitle">
      <h1>WARMING TEMPLATES</h1>
      <nav>
        <ol class="breadcrumb breadJa">
          <router-link :to="{ name: 'home' }" class="breadcrumb-item">Home</router-link>
          <router-link :to="{ name: 'warming-templates' }" class="breadcrumb-item">Templates</router-link>
        </ol>
      </nav>
    </div>

    <section class="section">
      <div class="row">
        
        <div class="col-lg-12">
          <div class="card card-chatroom">
            <div class="card-body">
              <h5 class="card-title card-title-chatroom">
                <i class="bi bi-file-earmark-code-fill me-2"></i>
                Templates Management
              </h5>

              <div class="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <button
                    class="btn btn-sm btn-chatroom btn-primary-chatroom me-2"
                    @click="openCreateModal"
                    :disabled="loading"
                  >
                    <i class="bi bi-plus-circle"></i>
                    Create Template
                  </button>
                  <button
                    class="btn btn-sm btn-chatroom btn-secondary-chatroom"
                    @click="fetchTemplates"
                    :disabled="loading"
                  >
                    <i class="bi bi-arrow-repeat"></i>
                  </button>
                </div>
                <div class="d-flex align-items-center gap-2">
                  <button
                    v-if="selectedTemplates.length > 0"
                    class="btn btn-sm btn-danger"
                    @click="bulkDeleteTemplates"
                  >
                    <i class="bi bi-trash"></i> Delete Selected ({{ selectedTemplates.length }})
                  </button>
                  <div style="width: 250px;">
                    <select class="form-select" v-model="categoryFilter" @change="fetchTemplates">
                      <option value="">All Categories</option>
                      <option v-for="cat in availableCategories" :key="cat" :value="cat">
                        {{ cat }}
                      </option>
                    </select>
                  </div>
                </div>
              </div>

              <div class="table-responsive">
                <table class="table table-hover shadow-sm table-bordered">
                  <thead class="table-primary">
                    <tr style="vertical-align:middle; text-align: center;">
                      <th style="width: 40px;">
                        <input type="checkbox" class="form-check-input" v-model="selectAll" @change="toggleSelectAll">
                      </th>
                      <th>No</th>
                      <th @click="sortBy('name')" style="cursor: pointer;">
                        Name <i :class="['bi', getSortIcon('name')]"></i>
                      </th>
                      <th @click="sortBy('category')" style="cursor: pointer;">
                        Category <i :class="['bi', getSortIcon('category')]"></i>
                      </th>
                      <th @click="sortBy('lines')" style="cursor: pointer;">
                        Lines <i :class="['bi', getSortIcon('lines')]"></i>
                      </th>
                      <th @click="sortBy('createdAt')" style="cursor: pointer;">
                        Created <i :class="['bi', getSortIcon('createdAt')]"></i>
                      </th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="loading">
                      <td colspan="7" class="text-center">
                        <div class="spinner-border text-primary"></div>
                      </td>
                    </tr>
                    <tr
                      v-else
                      v-for="(template, index) in paginatedTemplates"
                      :key="template.id"
                      style="vertical-align:middle;"
                    >
                      <td class="text-center">
                        <input 
                          type="checkbox" 
                          class="form-check-input" 
                          :value="template.id" 
                          v-model="selectedTemplates"
                        >
                      </td>
                      <td class="text-center">{{ (currentPage - 1) * itemsPerPage + index + 1 }}</td>
                      <td>{{ template.name }}</td>
                      <td class="text-center">
                        <span class="badge bg-success">{{ template.category }}</span>
                      </td>
                      <td class="text-center">{{ template.structure?.length || 0 }}</td>
                      <td class="text-center">{{ formatDate(template.createdAt) }}</td>
                      <td class="text-center" nowrap>
                        <button
                          class="btn btn-sm btn-outline-info me-1"
                          @click="viewTemplate(template)"
                          title="View"
                        >
                          <i class="bi bi-eye"></i>
                        </button>
                        <button
                          class="btn btn-sm btn-outline-primary me-1"
                          @click="duplicateTemplate(template)"
                          title="Duplicate"
                        >
                          <i class="bi bi-files"></i>
                        </button>
                        <button
                          class="btn btn-sm btn-outline-warning me-1"
                          @click="editTemplate(template)"
                          title="Edit"
                        >
                          <i class="bi bi-pencil"></i>
                        </button>
                        <button
                          class="btn btn-sm btn-outline-danger"
                          @click="deleteTemplate(template)"
                          title="Delete"
                        >
                          <i class="bi bi-trash"></i>
                        </button>
                      </td>
                    </tr>
                    <tr v-if="!loading && paginatedTemplates.length === 0">
                      <td colspan="7" class="text-center">No templates found</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              <!-- Pagination Controls -->
              <div class="d-flex justify-content-between align-items-center mt-3" v-if="!loading && sortedTemplates.length > 0">
                <div class="d-flex align-items-center gap-2">
                  <label class="mb-0 small">Show:</label>
                  <select class="form-select form-select-sm" style="width: auto;" v-model.number="itemsPerPage" @change="changeItemsPerPage">
                    <option v-for="option in perPageOptions" :key="option" :value="option">{{ option }}</option>
                  </select>
                  <span class="small text-muted">
                    Showing {{ (currentPage - 1) * itemsPerPage + 1 }} to {{ Math.min(currentPage * itemsPerPage, sortedTemplates.length) }} of {{ sortedTemplates.length }} templates
                  </span>
                </div>
                
                <nav>
                  <ul class="pagination pagination-sm mb-0">
                    <li class="page-item" :class="{ disabled: currentPage === 1 }">
                      <a class="page-link" href="#" @click.prevent="prevPage">
                        <i class="bi bi-chevron-left"></i>
                      </a>
                    </li>
                    
                    <li class="page-item" v-if="currentPage > 2">
                      <a class="page-link" href="#" @click.prevent="goToPage(1)">1</a>
                    </li>
                    <li class="page-item disabled" v-if="currentPage > 3">
                      <span class="page-link">...</span>
                    </li>
                    
                    <li class="page-item" v-if="currentPage > 1">
                      <a class="page-link" href="#" @click.prevent="goToPage(currentPage - 1)">{{ currentPage - 1 }}</a>
                    </li>
                    
                    <li class="page-item active">
                      <span class="page-link">{{ currentPage }}</span>
                    </li>
                    
                    <li class="page-item" v-if="currentPage < totalPages">
                      <a class="page-link" href="#" @click.prevent="goToPage(currentPage + 1)">{{ currentPage + 1 }}</a>
                    </li>
                    
                    <li class="page-item disabled" v-if="currentPage < totalPages - 2">
                      <span class="page-link">...</span>
                    </li>
                    <li class="page-item" v-if="currentPage < totalPages - 1">
                      <a class="page-link" href="#" @click.prevent="goToPage(totalPages)">{{ totalPages }}</a>
                    </li>
                    
                    <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                      <a class="page-link" href="#" @click.prevent="nextPage">
                        <i class="bi bi-chevron-right"></i>
                      </a>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>

    <!-- Create/Edit Modal - Professional Builder -->
    <div class="modal fade modal-smooth" id="templateModal" tabindex="-1">
      <div class="modal-dialog modal-custom-wide">
        <div class="modal-content">
          <div class="modal-header bg-primary text-white">
            <h5 class="modal-title">
              <i class="bi bi-code-square me-2"></i>
              {{ editMode ? 'Edit Template' : 'Create Template' }}
            </h5>
            <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="saveTemplate">
              <div class="row">
                <!-- LEFT: Quick Start + Builder (col-4) -->
                <div class="col-md-4">
                  <!-- Quick Start Examples -->
                  <div class="alert alert-info mb-2 shadow-sm">
                    <div class="d-flex justify-content-between align-items-center">
                      <div>
                        <i class="bi bi-lightbulb me-2"></i>
                        <strong>Quick Start</strong>
                      </div>
                      <div>
                        <button type="button" class="btn btn-sm btn-outline-primary me-1" @click="loadExample('casual')">
                          <i class="bi bi-chat-dots"></i>
                        </button>
                        <button type="button" class="btn btn-sm btn-outline-success" @click="loadExample('jual_beli')">
                          <i class="bi bi-cart"></i>
                        </button>
                      </div>
                    </div>
                  </div>

                  <!-- Spintax & Variables Info -->
                  <div class="alert alert-warning py-1 px-2 mb-3 shadow-sm">
                    <small>
                      <i class="bi bi-info-circle me-1"></i>
                      <strong>Spintax:</strong> {A|B} akan diacak otomatis | 
                      <strong>Var:</strong> {TIME_GREETING}, {DAY_NAME}, {DATE} akan di-render sistem
                    </small>
                  </div>

                  <!-- Message Lines Builder -->
                  <div class="d-flex justify-content-between align-items-center mb-2">
                    <h6 class="mb-0 small">
                      <i class="bi bi-list-ol me-1"></i>Builder
                    </h6>
                    <button type="button" class="btn btn-sm btn-success" @click="addLine">
                      <i class="bi bi-plus-circle"></i>
                    </button>
                  </div>

                  <div class="structure-builder" style="max-height: 450px; overflow-y: auto;">
                    <div 
                      v-for="(line, index) in structureArray"
                      :key="index"
                      class="card mb-2 shadow-sm fade-in-item"
                    >
                      <div class="card-body p-2">
                        <div class="d-flex justify-content-between align-items-start mb-2">
                          <span class="badge bg-secondary" style="font-size: 0.65rem;">L{{ index + 1 }}</span>
                          <button 
                            type="button" 
                            class="btn btn-sm btn-outline-danger py-0 px-1" 
                            @click="removeLine(index)"
                            :disabled="structureArray.length === 1"
                          >
                            <i class="bi bi-trash" style="font-size: 0.8rem;"></i>
                          </button>
                        </div>

                        <div class="mb-2">
                          <label class="form-label small mb-1" style="font-size: 0.75rem;">Actor</label>
                          <select class="form-select form-select-sm" v-model="line.actorRole" style="font-size: 0.8rem;">
                            <option value="ACTOR_A">A</option>
                            <option value="ACTOR_B">B</option>
                          </select>
                        </div>

                        <div class="mb-2">
                          <label class="form-label small mb-1" style="font-size: 0.75rem;">Type</label>
                          <select class="form-select form-select-sm" v-model="line.messageType" style="font-size: 0.8rem;">
                            <option value="">--</option>
                            <option value="GREETING">GREETING</option>
                            <option value="ANSWER_AND_QUESTION">ANS+Q</option>
                            <option value="QUESTION">QUESTION</option>
                            <option value="ANSWER">ANSWER</option>
                            <option value="STATEMENT">STATEMENT</option>
                          </select>
                        </div>

                        <div>
                          <label class="form-label small mb-1" style="font-size: 0.75rem;">Messages</label>
                          <textarea 
                            class="form-control form-control-sm font-monospace" 
                            v-model="line.messageOptionsText"
                            @input="updateMessageOptions(index)"
                            rows="2"
                            style="font-size: 0.75rem;"
                            placeholder="Hi|Hello&#10;{TIME_GREETING}"
                          ></textarea>
                        </div>
                      </div>
                    </div>

                    <div v-if="structureArray.length === 0" class="text-center text-muted py-3">
                      <i class="bi bi-inbox" style="font-size: 2rem;"></i>
                      <p class="small">Click + to add</p>
                    </div>
                  </div>
                </div>

                <!-- MIDDLE: Form + JSON (col-4) -->
                <div class="col-md-4">
                  <!-- Template Info -->
                  <div class="mb-2">
                    <label class="form-label fw-bold small">
                      <i class="bi bi-tag me-1"></i>Name <span class="text-danger">*</span>
                    </label>
                    <input 
                      type="text" 
                      class="form-control form-control-sm" 
                      :class="{ 'is-invalid': validationErrors.name }"
                      v-model="form.name" 
                      @input="clearValidationError('name')"
                      required
                      placeholder="e.g., Casual Chat"
                    >
                    <div v-if="validationErrors.name" class="invalid-feedback">
                      {{ validationErrors.name }}
                    </div>
                  </div>

                  <div class="mb-3">
                    <label class="form-label fw-bold small">
                      <i class="bi bi-folder me-1"></i>Category <span class="text-danger">*</span>
                    </label>
                    <input 
                      type="text" 
                      class="form-control form-control-sm" 
                      :class="{ 'is-invalid': validationErrors.category }"
                      v-model="form.category" 
                      @input="formatCategory"
                      required 
                      placeholder="e.g., casual"
                    >
                    <div v-if="validationErrors.category" class="invalid-feedback">
                      {{ validationErrors.category }}
                    </div>
                  </div>

                  <!-- JSON Preview -->
                  <div class="mb-2">
                    <div class="d-flex justify-content-between align-items-center mb-2">
                      <h6 class="mb-0 small">
                        <i class="bi bi-code-slash me-1"></i>JSON
                      </h6>
                      <button 
                        type="button" 
                        class="btn btn-sm btn-outline-secondary py-0 px-2" 
                        @click="copyJSON"
                        title="Copy"
                      >
                        <i class="bi bi-clipboard" style="font-size: 0.8rem;"></i>
                      </button>
                    </div>
                    
                    <div class="json-preview p-2 bg-dark text-light rounded shadow" style="max-height: 400px; overflow-y: auto; font-size: 0.7rem;">
                      <pre class="mb-0"><code>{{ formattedJSON }}</code></pre>
                    </div>
                  </div>
                </div>

                <!-- RIGHT: Live Preview (col-4) -->
                <div class="col-md-4">
                  <div class="mb-2">
                    <h6 class="mb-2 small">
                      <i class="bi bi-eye me-1"></i>Live Preview
                      <span class="badge bg-success ms-2" style="font-size: 0.65rem;">Real Output</span>
                    </h6>
                  </div>

                  <div class="live-preview-container p-3 bg-light rounded shadow" style="max-height: 550px; overflow-y: auto;">
                    <div v-if="structureArray.length === 0" class="text-center text-muted py-5">
                      <i class="bi bi-chat-quote" style="font-size: 3rem;"></i>
                      <p class="small mt-2">Preview will appear here</p>
                    </div>

                    <div v-else>
                      <transition-group name="preview-fade" tag="div">
                        <div 
                          v-for="(line, index) in structureArray" 
                          :key="'preview-' + index"
                          class="mb-3"
                        >
                          <div class="d-flex align-items-start">
                            <div 
                              class="preview-bubble p-2 rounded shadow-sm"
                              :class="line.actorRole === 'ACTOR_A' ? 'bg-primary text-white ms-auto' : 'bg-white'"
                              style="max-width: 80%; font-size: 0.85rem;"
                            >
                              <div class="mb-1">
                                <span class="badge" :class="line.actorRole === 'ACTOR_A' ? 'bg-light text-dark' : 'bg-primary'" style="font-size: 0.6rem;">
                                  {{ line.actorRole }} - {{ line.messageType || 'NO TYPE' }}
                                </span>
                              </div>
                              <div v-for="(msg, msgIdx) in renderPreview(line.messageOptions)" :key="msgIdx" class="mb-1">
                                {{ msg }}
                              </div>
                            </div>
                          </div>
                        </div>
                      </transition-group>
                    </div>
                  </div>

                  <div class="mt-2">
                    <small class="text-muted">
                      <i class="bi bi-info-circle me-1"></i>
                      Preview shows random spintax & real variables
                    </small>
                  </div>
                </div>
              </div>

            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-sm btn-chatroom btn-secondary-chatroom" data-bs-dismiss="modal">
              <i class="bi bi-x-circle"></i> Cancel
            </button>
            <button type="button" class="btn btn-sm btn-chatroom btn-primary-chatroom" @click="saveTemplate" :disabled="loading">
              <span v-if="loading" class="spinner-border spinner-border-sm me-1"></span>
              <i v-else class="bi bi-save"></i> Save Template
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- View Modal - Template Structure Preview -->
    <div class="modal fade modal-smooth" id="viewModal" tabindex="-1">
      <div class="modal-dialog modal-xl">
        <div class="modal-content border-0 shadow-lg">
          <!-- Header -->
          <div class="modal-header bg-gradient-primary text-white border-0">
            <div class="d-flex align-items-center">
              <div class="preview-icon-wrapper me-3">
                <i class="bi bi-file-earmark-text-fill"></i>
              </div>
              <div>
                <h5 class="modal-title mb-0 fw-bold">{{ viewingTemplate?.name }}</h5>
                <small class="opacity-75">
                  <i class="bi bi-folder me-1"></i>{{ viewingTemplate?.category }}
                  <span class="mx-2">•</span>
                  <i class="bi bi-list-ol me-1"></i>{{ viewingTemplate?.structure?.length || 0 }} lines
                </small>
              </div>
            </div>
            <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal"></button>
          </div>
          
          <!-- Body -->
          <div class="modal-body p-0">
            <div class="row g-0">
              <!-- Left: Template Structure Preview -->
              <div class="col-lg-8 border-end">
                <div class="template-structure-container">
                  <div class="structure-header">
                    <h6 class="mb-0">
                      <i class="bi bi-diagram-3 me-2"></i>Template Structure
                    </h6>
                    <span class="badge bg-info">{{ viewingTemplate?.structure?.length || 0 }} Lines</span>
                  </div>

                  <div class="structure-content">
                    <!-- Empty State -->
                    <div v-if="!viewingTemplate?.structure || viewingTemplate.structure.length === 0" class="empty-structure-state">
                      <i class="bi bi-inbox"></i>
                      <p>No structure defined</p>
                    </div>

                    <!-- Structure Lines -->
                    <transition-group name="structure-fade" tag="div" v-else>
                      <div 
                        v-for="(line, index) in (viewingTemplate?.structure || [])" 
                        :key="'structure-' + index"
                        class="structure-line-card"
                      >
                        <div class="line-header">
                          <div class="d-flex align-items-center gap-2">
                            <span class="line-number">Line {{ index + 1 }}</span>
                            <span class="actor-badge" :class="line.actorRole === 'ACTOR_A' ? 'actor-a' : 'actor-b'">
                              {{ line.actorRole === 'ACTOR_A' ? 'Actor A' : 'Actor B' }}
                            </span>
                            <span class="type-badge" v-if="line.messageType">
                              {{ formatMessageType(line.messageType) }}
                            </span>
                          </div>
                        </div>

                        <div class="line-content">
                          <div class="content-section">
                            <label class="content-label">Message Options:</label>
                            <div class="message-options">
                              <div 
                                v-for="(option, optIdx) in line.messageOptions" 
                                :key="optIdx"
                                class="message-option"
                              >
                                <span class="option-number">{{ optIdx + 1 }}.</span>
                                <code class="option-text">{{ option }}</code>
                              </div>
                            </div>
                          </div>

                          <div class="content-section mt-3">
                            <label class="content-label">Preview Output:</label>
                            <div class="preview-output">
                              <div 
                                v-for="(msg, msgIdx) in renderPreview(line.messageOptions)" 
                                :key="msgIdx"
                                class="preview-message"
                              >
                                <i class="bi bi-arrow-right-short text-primary"></i>
                                {{ msg }}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </transition-group>
                  </div>
                </div>
              </div>

              <!-- Right: Template Details & JSON -->
              <div class="col-lg-4">
                <div class="template-details-panel">
                  <!-- Info Section -->
                  <div class="details-section">
                    <h6 class="section-title">
                      <i class="bi bi-info-circle me-2"></i>Information
                    </h6>
                    <div class="info-list">
                      <div class="info-item">
                        <span class="info-label">Created</span>
                        <span class="info-value">{{ formatDate(viewingTemplate?.createdAt) }}</span>
                      </div>
                      <div class="info-item">
                        <span class="info-label">Updated</span>
                        <span class="info-value">{{ formatDate(viewingTemplate?.updatedAt) }}</span>
                      </div>
                      <div class="info-item">
                        <span class="info-label">Category</span>
                        <span class="info-value">
                          <span class="badge bg-success">{{ viewingTemplate?.category }}</span>
                        </span>
                      </div>
                    </div>
                  </div>

                  <!-- Variables Info -->
                  <div class="details-section">
                    <h6 class="section-title">
                      <i class="bi bi-braces me-2"></i>Variables
                    </h6>
                    <div class="variables-list">
                      <div class="variable-item">
                        <code>{TIME_GREETING}</code>
                        <span class="variable-desc">{{ getCurrentTimeGreeting() }}</span>
                      </div>
                      <div class="variable-item">
                        <code>{DAY_NAME}</code>
                        <span class="variable-desc">{{ getCurrentDayName() }}</span>
                      </div>
                      <div class="variable-item">
                        <code>{DATE}</code>
                        <span class="variable-desc">{{ getCurrentDate() }}</span>
                      </div>
                      <div class="variable-item">
                        <code>{A|B|C}</code>
                        <span class="variable-desc">Spintax</span>
                      </div>
                    </div>
                  </div>

                  <!-- JSON Structure -->
                  <div class="details-section">
                    <div class="d-flex justify-content-between align-items-center mb-3">
                      <h6 class="section-title mb-0">
                        <i class="bi bi-code-slash me-2"></i>JSON
                      </h6>
                      <button 
                        type="button" 
                        class="btn btn-sm btn-outline-primary"
                        @click="copyViewJSON"
                      >
                        <i class="bi bi-clipboard"></i>
                      </button>
                    </div>
                    <div class="json-viewer">
                      <pre class="mb-0"><code>{{ formatViewJSON() }}</code></pre>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="modal-footer border-0 bg-light">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
              <i class="bi bi-x-circle me-1"></i>Close
            </button>
          </div>
        </div>
      </div>
    </div>

  </main>
</template>

<script>
import axios from 'axios';

export default {
  data() {
    return {
      templates: [],
      loading: false,
      categoryFilter: '',
      availableCategories: [],
      baseUrl: process.env.VUE_APP_BE_APP_BASE_URL,
      
      // Sorting
      sortColumn: 'createdAt',
      sortDirection: 'desc',
      
      // Pagination
      currentPage: 1,
      itemsPerPage: 10,
      perPageOptions: [10, 25, 50],
      
      // Bulk Delete
      selectedTemplates: [],
      selectAll: false,
      
      editMode: false,
      currentTemplate: null,
      viewingTemplate: null,
      form: {
        name: '',
        category: ''
      },
      structureJson: '',
      structureArray: [],
      validationErrors: {
        name: '',
        category: ''
      }
    };
  },
  
  computed: {
    formattedJSON() {
      try {
        const structure = this.structureArray.map(line => ({
          actorRole: line.actorRole,
          messageType: line.messageType,
          messageOptions: line.messageOptions
        }));
        return JSON.stringify(structure, null, 2);
      } catch (e) {
        return '[]';
      }
    },
    
    filteredTemplates() {
      let filtered = [...this.templates];
      if (this.categoryFilter) {
        filtered = filtered.filter(t => t.category === this.categoryFilter);
      }
      return filtered;
    },
    
    sortedTemplates() {
      const sorted = [...this.filteredTemplates];
      sorted.sort((a, b) => {
        let aVal = a[this.sortColumn];
        let bVal = b[this.sortColumn];
        
        if (this.sortColumn === 'lines') {
          aVal = a.structure?.length || 0;
          bVal = b.structure?.length || 0;
        }
        
        if (this.sortColumn === 'createdAt' || this.sortColumn === 'updatedAt') {
          aVal = new Date(aVal);
          bVal = new Date(bVal);
        }
        
        if (typeof aVal === 'string') {
          aVal = aVal.toLowerCase();
          bVal = bVal.toLowerCase();
        }
        
        if (aVal < bVal) return this.sortDirection === 'asc' ? -1 : 1;
        if (aVal > bVal) return this.sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
      return sorted;
    },
    
    paginatedTemplates() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.sortedTemplates.slice(start, end);
    },
    
    totalPages() {
      return Math.ceil(this.sortedTemplates.length / this.itemsPerPage);
    },
    
    isAllSelected() {
      return this.paginatedTemplates.length > 0 && 
             this.paginatedTemplates.every(t => this.selectedTemplates.includes(t.id));
    }
  },
  
  mounted() {
    this.fetchTemplates();
    this.fetchCategories();
  },
  
  methods: {
    async fetchTemplates() {
      this.loading = true;
      try {
        const params = this.categoryFilter ? { category: this.categoryFilter } : {};
        const res = await axios.get(`${this.baseUrl}/api/warming/templates`, { params });
        this.templates = res.data.data.templates || [];
      } catch (error) {
        console.error('Failed to fetch templates:', error);
        this.$swal.fire('Error', 'Failed to load templates', 'error');
      } finally {
        this.loading = false;
      }
    },
    
    async fetchCategories() {
      try {
        const res = await axios.get(`${this.baseUrl}/api/warming/templates`);
        const templates = res.data.data.templates || [];
        this.availableCategories = [...new Set(templates.map(t => t.category))].filter(Boolean).sort();
      } catch (error) {
        console.error('Failed to fetch categories:', error);
        this.availableCategories = [];
      }
    },
    
    formatCategory() {
      this.form.category = this.form.category
        .toLowerCase()
        .replace(/\s+/g, '_')
        .replace(/[^a-z0-9_]/g, '');
      this.clearValidationError('category');
    },
    
    clearValidationError(field) {
      this.validationErrors[field] = '';
    },
    
    openCreateModal() {
      this.editMode = false;
      this.currentTemplate = null;
      this.form = { name: '', category: '' };
      this.structureArray = [{
        actorRole: 'ACTOR_A',
        messageType: 'GREETING',
        messageOptions: ['{TIME_GREETING}', '{Halo|Hi|Hey}', 'Hari {DAY_NAME} tanggal {DATE}'],
        messageOptionsText: '{TIME_GREETING}\n{Halo|Hi|Hey}\nHari {DAY_NAME} tanggal {DATE}'
      }];
      const modal = new bootstrap.Modal(document.getElementById('templateModal'));
      modal.show();
    },
    
    editTemplate(template) {
      this.editMode = true;
      this.currentTemplate = template;
      this.form = {
        name: template.name,
        category: template.category
      };
      // Convert structure to array with text
      this.structureArray = (template.structure || []).map(line => ({
        ...line,
        messageOptionsText: (line.messageOptions || []).join('\n')
      }));
      const modal = new bootstrap.Modal(document.getElementById('templateModal'));
      modal.show();
    },
    
    addLine() {
      this.structureArray.push({
        actorRole: 'ACTOR_A',
        messageType: '',
        messageOptions: [],
        messageOptionsText: ''
      });
    },
    
    removeLine(index) {
      if (this.structureArray.length > 1) {
        this.structureArray.splice(index, 1);
      }
    },
    
    updateMessageOptions(index) {
      const text = this.structureArray[index].messageOptionsText;
      this.structureArray[index].messageOptions = text
        .split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0);
    },
    
    loadExample(type) {
      if (type === 'casual') {
        this.form.name = 'Casual Chat Template';
        this.form.category = 'casual';
        this.structureArray = [
          {
            actorRole: 'ACTOR_A',
            messageType: 'GREETING',
            messageOptions: ['{TIME_GREETING}', '{Halo|Hi|Hey}'],
            messageOptionsText: '{TIME_GREETING}\n{Halo|Hi|Hey}'
          },
          {
            actorRole: 'ACTOR_B',
            messageType: 'ANSWER',
            messageOptions: ['{Halo juga|Hi juga|Hey}', 'Ada yang bisa dibantu?'],
            messageOptionsText: '{Halo juga|Hi juga|Hey}\nAda yang bisa dibantu?'
          },
          {
            actorRole: 'ACTOR_A',
            messageType: 'QUESTION',
            messageOptions: ['{Apa kabar {DAY_NAME} ini?|Gimana kabarnya hari {DAY_NAME}?}'],
            messageOptionsText: '{Apa kabar {DAY_NAME} ini?|Gimana kabarnya hari {DAY_NAME}?}'
          }
        ];
      } else if (type === 'jual_beli') {
        this.form.name = 'Jual Beli Motor Template';
        this.form.category = 'jual_beli_motor';
        this.structureArray = [
          {
            actorRole: 'ACTOR_A',
            messageType: 'GREETING',
            messageOptions: ['{TIME_GREETING}', 'Halo {bro|gan}'],
            messageOptionsText: '{TIME_GREETING}\nHalo {bro|gan}'
          },
          {
            actorRole: 'ACTOR_A',
            messageType: 'QUESTION',
            messageOptions: ['Motornya masih {ada|ready}?', 'Harga berapa {bro|gan}?'],
            messageOptionsText: 'Motornya masih {ada|ready}?\nHarga berapa {bro|gan}?'
          },
          {
            actorRole: 'ACTOR_B',
            messageType: 'ANSWER_AND_QUESTION',
            messageOptions: ['{Masih|Iya} {ada|ready} {bro|gan}', 'Mau lihat kapan? Tanggal {DATE}?'],
            messageOptionsText: '{Masih|Iya} {ada|ready} {bro|gan}\nMau lihat kapan? Tanggal {DATE}?'
          }
        ];
      }
    },
    
    copyJSON() {
      navigator.clipboard.writeText(this.formattedJSON);
      this.$swal.fire({
        icon: 'success',
        title: 'Copied!',
        text: 'JSON copied to clipboard',
        timer: 1500,
        showConfirmButton: false
      });
    },
    
    renderPreview(messageOptions) {
      if (!messageOptions || messageOptions.length === 0) return ['(empty)'];
      
      const now = new Date();
      const hour = now.getHours();
      const dayNames = ['Minggu', 'Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu'];
      const dayName = dayNames[now.getDay()];
      const date = now.toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });
      
      let timeGreeting = 'Selamat Pagi';
      if (hour >= 11 && hour < 15) timeGreeting = 'Selamat Siang';
      else if (hour >= 15 && hour < 18) timeGreeting = 'Selamat Sore';
      else if (hour >= 18 || hour < 4) timeGreeting = 'Selamat Malam';
      
      return messageOptions.map(option => {
        let text = option;
        
        // Replace variables
        text = text.replace(/{TIME_GREETING}/g, timeGreeting);
        text = text.replace(/{DAY_NAME}/g, dayName);
        text = text.replace(/{DATE}/g, date);
        
        // Render spintax - pick random option
        text = text.replace(/{([^}]+)}/g, (match, content) => {
          const options = content.split('|');
          return options[Math.floor(Math.random() * options.length)];
        });
        
        return text;
      });
    },
    
    viewTemplate(template) {
      this.viewingTemplate = template;
      const modal = new bootstrap.Modal(document.getElementById('viewModal'));
      modal.show();
    },
    
    async saveTemplate() {
      // Validate
      this.validationErrors = { name: '', category: '' };
      let isValid = true;
      
      if (!this.form.name || this.form.name.trim() === '') {
        this.validationErrors.name = 'Template name is required';
        isValid = false;
      }
      
      if (!this.form.category || this.form.category.trim() === '') {
        this.validationErrors.category = 'Category is required';
        isValid = false;
      }
      
      if (!isValid) {
        this.$swal.fire('Validation Error', 'Please fill all required fields', 'error');
        return;
      }
      
      this.loading = true;
      try {
        // Build structure from array
        const structure = this.structureArray.map(line => ({
          actorRole: line.actorRole,
          messageType: line.messageType,
          messageOptions: line.messageOptions
        }));
        
        const payload = { ...this.form, structure };
        
        if (this.editMode) {
          await axios.put(`${this.baseUrl}/api/warming/templates/${this.currentTemplate.id}`, payload);
          this.$swal.fire('Success', 'Template updated!', 'success');
        } else {
          await axios.post(`${this.baseUrl}/api/warming/templates`, payload);
          this.$swal.fire('Success', 'Template created!', 'success');
        }
        
        bootstrap.Modal.getInstance(document.getElementById('templateModal')).hide();
        this.fetchTemplates();
        this.fetchCategories();
      } catch (error) {
        console.error('Save error:', error);
        this.$swal.fire('Error', error.response?.data?.message || 'Failed to save', 'error');
      } finally {
        this.loading = false;
      }
    },
    
    async deleteTemplate(template) {
      const result = await this.$swal.fire({
        title: 'Delete Template?',
        text: `Delete "${template.name}"?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#dc3545',
        confirmButtonText: 'Yes, delete'
      });
      
      if (!result.isConfirmed) return;
      
      try {
        await axios.delete(`${this.baseUrl}/api/warming/templates/${template.id}`);
        this.$swal.fire('Deleted!', 'Template deleted', 'success');
        this.fetchTemplates();
      } catch (error) {
        this.$swal.fire('Error', 'Failed to delete', 'error');
      }
    },
    
    // Sorting Methods
    sortBy(column) {
      if (this.sortColumn === column) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortColumn = column;
        this.sortDirection = 'asc';
      }
      this.currentPage = 1; // Reset to first page when sorting
    },
    
    getSortIcon(column) {
      if (this.sortColumn !== column) return 'bi-arrow-down-up';
      return this.sortDirection === 'asc' ? 'bi-sort-up' : 'bi-sort-down';
    },
    
    // Pagination Methods
    goToPage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
      }
    },
    
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    },
    
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },
    
    changeItemsPerPage() {
      this.currentPage = 1; // Reset to first page when changing items per page
    },
    
    // Duplicate & Bulk Delete Methods
    async duplicateTemplate(template) {
      try {
        const payload = {
          name: `${template.name} (Copy)`,
          category: template.category,
          structure: template.structure
        };
        
        await axios.post(`${this.baseUrl}/api/warming/templates`, payload);
        this.$swal.fire('Success', 'Template duplicated!', 'success');
        this.fetchTemplates();
      } catch (error) {
        this.$swal.fire('Error', 'Failed to duplicate template', 'error');
      }
    },
    
    async bulkDeleteTemplates() {
      if (this.selectedTemplates.length === 0) return;
      
      const result = await this.$swal.fire({
        title: 'Delete Templates?',
        text: `Delete ${this.selectedTemplates.length} selected template(s)?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#dc3545',
        confirmButtonText: 'Yes, delete all'
      });
      
      if (!result.isConfirmed) return;
      
      try {
        // Delete each template
        await Promise.all(
          this.selectedTemplates.map(id => 
            axios.delete(`${this.baseUrl}/api/warming/templates/${id}`)
          )
        );
        
        this.$swal.fire('Deleted!', `${this.selectedTemplates.length} template(s) deleted`, 'success');
        this.selectedTemplates = [];
        this.selectAll = false;
        this.fetchTemplates();
      } catch (error) {
        this.$swal.fire('Error', 'Failed to delete some templates', 'error');
      }
    },
    
    toggleSelectAll() {
      if (this.selectAll) {
        this.selectedTemplates = this.paginatedTemplates.map(t => t.id);
      } else {
        this.selectedTemplates = [];
      }
    },
    
    
    
    formatDate(date) {
      if (!date) return 'N/A';
      return new Date(date).toLocaleDateString('id-ID');
    },
    
    formatDateForChat(date) {
      const now = new Date();
      const inputDate = new Date(date);
      
      if (inputDate.toDateString() === now.toDateString()) {
        return 'Today';
      }
      
      return inputDate.toLocaleDateString('id-ID', { 
        day: 'numeric', 
        month: 'long', 
        year: 'numeric' 
      });
    },
    
    formatMessageType(type) {
      const typeMap = {
        'GREETING': 'Greeting',
        'ANSWER_AND_QUESTION': 'Answer + Question',
        'QUESTION': 'Question',
        'ANSWER': 'Answer',
        'STATEMENT': 'Statement'
      };
      return typeMap[type] || type;
    },
    
    getRandomTime() {
      const hour = Math.floor(Math.random() * 12) + 8;
      const minute = Math.floor(Math.random() * 60);
      return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
    },
    
    getCurrentTimeGreeting() {
      const hour = new Date().getHours();
      if (hour >= 11 && hour < 15) return 'Selamat Siang';
      if (hour >= 15 && hour < 18) return 'Selamat Sore';
      if (hour >= 18 || hour < 4) return 'Selamat Malam';
      return 'Selamat Pagi';
    },
    
    getCurrentDayName() {
      const dayNames = ['Minggu', 'Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu'];
      return dayNames[new Date().getDay()];
    },
    
    getCurrentDate() {
      return new Date().toLocaleDateString('id-ID', { 
        day: 'numeric', 
        month: 'long', 
        year: 'numeric' 
      });
    },
    
    formatViewJSON() {
      if (!this.viewingTemplate?.structure) return '[]';
      return JSON.stringify(this.viewingTemplate.structure, null, 2);
    },
    
    copyViewJSON() {
      const json = this.formatViewJSON();
      navigator.clipboard.writeText(json);
      this.$swal.fire({
        icon: 'success',
        title: 'Copied!',
        text: 'JSON structure copied to clipboard',
        timer: 1500,
        showConfirmButton: false
      });
    }
  }
};
</script>

